<template>
  <div class="personnel">
    <OmxList
        :data="filteredPersonnelData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasSearch ? 'filter-variant' : 'account-multiple-outline'"
        :empty-title="hasSearch ? $t('omx-list.empty-title') : $t('personnel-view-empty.title')"
        :empty-body="hasSearch ? null : $t('personnel-view-empty.body')"
        :skeleton-columns="[1.6, 1, 1, 1]"
    >
      <template #actions>
        <OmxPermissionButton
            :allowed="canCreate"
            type="is-primary"
            icon-left="plus-thick"
            @click="addNewPersonalClicked"
        >{{ $t('personnel-view.new-personal-button') }}</OmxPermissionButton>
      </template>

      <template #empty-actions>
        <OmxPermissionButton
            v-if="!hasSearch"
            :allowed="canCreate"
            type="is-primary"
            icon-left="plus-thick"
            @click="addNewPersonalClicked"
        >{{ $t('personnel-view.new-personal-button') }}</OmxPermissionButton>
        <b-button v-if="hasSearch" @click="search = ''">{{ $t('omx-list.clear-selection') }}</b-button>
      </template>

      <b-table-column field="fullname" :label="$t('personnel-view.personal-fullname-column')" v-slot="props" sortable>
        <router-link
            :to="{ name: 'PersonalDetails', params: { project_id: project_id, personal_id: props.row.personal.document_id, is_template: is_template }}"
            class="omx-row-title"
        >{{ getAdjustedTitle(props.row.fullname) }}</router-link>
      </b-table-column>

      <b-table-column field="personal.group" :label="$t('personnel-view.personal-group-column')" v-slot="props" sortable>
        <span :class="props.row.personal.group === 'internal' ? 'internal-text' : 'external-text'">
          {{ groupLabel(props.row.personal.group) }}
        </span>
      </b-table-column>

      <b-table-column field="personal.agency" :label="$t('personnel-view.personal-agency-column')" v-slot="props" sortable>
        {{ props.row.personal.agency }}
      </b-table-column>

      <b-table-column field="personal.category" :label="$t('personnel-view.personal-category-column')" v-slot="props" sortable>
        {{ props.row.personal.category }}
      </b-table-column>
    </OmxList>

    <b-modal :width="640" v-model="isPersonalDetailsModalActiveLocal" trap-focus :destroy-on-hide="true" aria-role="dialog" aria-modal>
      <template #default="props">
        <PersonalDetails :project_id="project_id" personal_id="new" @close="props.close" @personalAdded="readPersonnelData" />
      </template>
    </b-modal>
  </div>
</template>

<script>
import PersonalDetails from "@/components/PersonalDetails";
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Personnel",
  components: { OmxList, PersonalDetails },
  props: {
    project_id: { type: String },
    is_template: { type: Boolean }
  },
  data() {
    return {
      personnelData: [],
      isLoading: false,
      isPersonalDetailsModalActiveLocal: false,
      search: ""
    };
  },
  computed: {
    canCreate() {
      return (!this.is_template && this.currentUserAccess('PersonalDetails', this.UserActions.CREATE, this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', this.UserActions.CREATE));
    },
    hasSearch() { return this.search.trim() !== ""; },
    filteredPersonnelData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.personnelData;
      return this.personnelData.filter(row => {
        const p = row.personal || {};
        return [row.fullname, p.agency, p.category, this.groupLabel(p.group)].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
    }
  },
  methods: {
    groupLabel(group) {
      const opt = this.personalGroupOptions.find(o => o.id === group);
      return opt ? this.$t(opt.translationKey) : group;
    },
    readPersonnelData() {
      this.isPersonalDetailsModalActiveLocal = false;
      this.isLoading = true;
      this.personnelData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getPersonnelFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getPersonnelFunction({ action: "get_personnel", project_id: this.project_id, fill_meta: true })
          .then((result) => {
            if (result.data.personnel.length > 0) {
              result.data["personnel"].forEach((personal) => {
                this.personnelData.push({
                  personal,
                  fullname: this.getFullName(personal.name)
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message);
            console.log("Error getting personnel:", error?.code);
            this.isLoading = false;
          });
    },
    addNewPersonalClicked() { this.isPersonalDetailsModalActiveLocal = true; }
  },
  mounted() {
    this.readPersonnelData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.personnel { margin-top: 16px; }
.omx-row-title {
  color: $omx-brand-600;
  font-weight: 600;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
